import type { WheelEntry } from '../types/wheel';
import { MAX_ENTRIES, MAX_SHARE_URL_BYTES } from './limits';
import { clampWeight, DEFAULT_WEIGHT } from './weights';

export const MAX_ENTRY_NAME_LENGTH = 200;
export const MAX_WHEEL_TITLE_LENGTH = 120;
/**
 * Maximum encoded thumbnail length, including the data-URL prefix.
 *
 * Camera originals can be several megabytes and would exhaust localStorage
 * after only one or two entries. Uploads are resized before being added, while
 * this boundary also protects every untrusted storage and share-link read.
 */
export const MAX_ENTRY_IMAGE_DATA_URL_LENGTH = 150_000;

const HEX_COLOR = /^#[0-9a-f]{6}$/i;
const IMAGE_DATA_URL = /^data:image\/(png|jpeg|webp);base64,([A-Za-z0-9+/]*={0,2})$/;
const CANONICAL_BASE64 = /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;

function asciiAt(bytes: Uint8Array, offset: number, expected: string): boolean {
  if (offset < 0 || offset + expected.length > bytes.length) return false;
  for (let i = 0; i < expected.length; i++) {
    if (bytes[offset + i] !== expected.charCodeAt(i)) return false;
  }
  return true;
}

/**
 * Accept only bounded, canonical base64 data URLs for formats the uploader and
 * canvas renderer support. Magic-byte checks stop a label from smuggling an
 * arbitrary base64 payload behind an image MIME type.
 */
export function isValidImageDataUrl(value: unknown): value is string {
  if (typeof value !== 'string' || value.length > MAX_ENTRY_IMAGE_DATA_URL_LENGTH) return false;

  const match = IMAGE_DATA_URL.exec(value);
  if (!match) return false;
  const [, format, payload] = match;
  if (!payload || !CANONICAL_BASE64.test(payload)) return false;

  let binary: string;
  try {
    binary = atob(payload);
  } catch {
    return false;
  }
  const bytes = Uint8Array.from(binary, char => char.charCodeAt(0));

  if (format === 'png') {
    return bytes.length >= 24 &&
      bytes[0] === 0x89 && asciiAt(bytes, 1, 'PNG') &&
      bytes[4] === 0x0d && bytes[5] === 0x0a && bytes[6] === 0x1a && bytes[7] === 0x0a &&
      asciiAt(bytes, 12, 'IHDR') && asciiAt(bytes, bytes.length - 8, 'IEND');
  }
  if (format === 'jpeg') {
    return bytes.length >= 4 &&
      bytes[0] === 0xff && bytes[1] === 0xd8 &&
      bytes[bytes.length - 2] === 0xff && bytes[bytes.length - 1] === 0xd9;
  }
  // WebP's RIFF header stores the file size minus its first eight bytes.
  if (bytes.length < 12 || !asciiAt(bytes, 0, 'RIFF') || !asciiAt(bytes, 8, 'WEBP')) return false;
  const declaredSize = bytes[4] | (bytes[5] << 8) | (bytes[6] << 16) | (bytes[7] << 24);
  return declaredSize >>> 0 === bytes.length - 8;
}

export function normalizeTitle(value: unknown, fallback = 'My Wheel'): string {
  if (typeof value !== 'string') return fallback;
  const title = value.trim().slice(0, MAX_WHEEL_TITLE_LENGTH);
  return title || fallback;
}

/**
 * Validate entry data before it reaches rendering or selection code.
 * Invalid rows are discarded for recoverable browser storage; callers that
 * need all-or-nothing validation can compare the returned length.
 */
export function normalizeEntries(value: unknown): WheelEntry[] | null {
  if (!Array.isArray(value)) return null;

  const entries: WheelEntry[] = [];
  for (const raw of value.slice(0, MAX_ENTRIES)) {
    if (typeof raw === 'string') {
      const name = raw.trim().slice(0, MAX_ENTRY_NAME_LENGTH);
      if (name) entries.push({ name, weight: DEFAULT_WEIGHT });
      continue;
    }
    if (!raw || typeof raw !== 'object') continue;
    const candidate = raw as Record<string, unknown>;
    if (typeof candidate.name !== 'string') continue;
    const name = candidate.name.trim().slice(0, MAX_ENTRY_NAME_LENGTH);
    if (!name) continue;

    const entry: WheelEntry = { name, weight: clampWeight(candidate.weight ?? DEFAULT_WEIGHT) };
    if (typeof candidate.color === 'string' && HEX_COLOR.test(candidate.color)) {
      entry.color = candidate.color.toLowerCase();
    }
    if (isValidImageDataUrl(candidate.imageDataUrl)) {
      entry.imageDataUrl = candidate.imageDataUrl;
    }
    entries.push(entry);
  }
  return entries;
}

export function encodeShare(
  title: string,
  entries: WheelEntry[],
  centerImageDataUrl?: string | null,
): string {
  const hasEntryImages = entries.some(entry => isValidImageDataUrl(entry.imageDataUrl));
  const hasCenterImage = isValidImageDataUrl(centerImageDataUrl);
  const hasImages = hasEntryImages || hasCenterImage;
  const data: Record<string, unknown> = {
    v: hasImages ? 3 : 2,
    t: title,
    n: entries.map(entry => entry.name),
    w: entries.map(entry => entry.weight),
  };
  if (entries.some(entry => entry.color)) {
    data.c = entries.map(entry => entry.color ?? '');
  }
  if (hasImages) {
    data.i = entries.map(entry => isValidImageDataUrl(entry.imageDataUrl) ? entry.imageDataUrl : '');
  }
  if (hasCenterImage) {
    data.m = centerImageDataUrl;
  }
  return btoa(encodeURIComponent(JSON.stringify(data)));
}

export function decodeShare(encoded: string): {
  t: string;
  entries: WheelEntry[];
  centerImageDataUrl?: string;
} | null {
  // Base64 is ASCII, so character count and encoded byte count are equivalent.
  if (!encoded || encoded.length > MAX_SHARE_URL_BYTES) return null;

  try {
    const parsed: unknown = JSON.parse(decodeURIComponent(atob(encoded)));
    if (!parsed || typeof parsed !== 'object') return null;
    const data = parsed as Record<string, unknown>;
    if (data.v !== 1 && data.v !== 2 && data.v !== 3) return null;
    if (!Array.isArray(data.n) || data.n.length > MAX_ENTRIES) return null;
    if ((data.v === 2 || data.v === 3) && data.w !== undefined && !Array.isArray(data.w)) return null;
    if (data.c !== undefined && !Array.isArray(data.c)) return null;
    if (data.v === 3 && (!Array.isArray(data.i) || data.i.length !== data.n.length)) return null;
    if (data.m !== undefined && (data.v !== 3 || !isValidImageDataUrl(data.m))) return null;

    const names = data.n;
    if (names.some(name => typeof name !== 'string' || !name.trim())) return null;
    const weights = Array.isArray(data.w) ? data.w : [];
    const colors = Array.isArray(data.c) ? data.c : [];
    const images = data.v === 3 && Array.isArray(data.i) ? data.i : [];
    if (images.some(image => image !== '' && !isValidImageDataUrl(image))) return null;
    const rawEntries = names.map((name, index) => ({
      name,
      weight: data.v === 2 || data.v === 3 ? weights[index] ?? DEFAULT_WEIGHT : DEFAULT_WEIGHT,
      color: colors[index],
      imageDataUrl: images[index],
    }));
    const entries = normalizeEntries(rawEntries);
    if (!entries || entries.length !== names.length) return null;

    const result: {
      t: string;
      entries: WheelEntry[];
      centerImageDataUrl?: string;
    } = { t: normalizeTitle(data.t), entries };
    if (data.v === 3 && isValidImageDataUrl(data.m)) {
      result.centerImageDataUrl = data.m;
    }
    return result;
  } catch {
    return null;
  }
}

/**
 * Build a share or embed URL without letting standard Base64 characters be
 * reinterpreted by URLSearchParams. In particular, an unescaped `+` in a query
 * string is decoded as a space and makes an otherwise valid wheel unreadable.
 */
export function buildSharedWheelUrl(
  origin: string,
  path: string,
  encoded: string,
  params: Readonly<Record<string, string | number | boolean | null | undefined>> = {},
): string {
  const url = new URL(path, origin);
  url.searchParams.set('shared', encoded);
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

/** Approximate the full canonical share URL, including its fixed URL prefix. */
export function shareUrlByteLength(encoded: string): number {
  return new TextEncoder().encode(buildSharedWheelUrl('https://thenamewheel.com', '/', encoded)).length;
}
